import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import type { ScrollFadeProps } from "./types";

const FADE_BASE =
  "pointer-events-none absolute inset-x-0 z-10 h-10 opacity-0 transition-opacity duration-200";

// Hide a fade once within this many pixels of the edge.
const EDGE_THRESHOLD = 24;

export const ScrollFade = ({ children, className }: ScrollFadeProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const topFadeRef = useRef<HTMLDivElement>(null);
  const bottomFadeRef = useRef<HTMLDivElement>(null);

  const updateFades = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const { scrollTop, scrollHeight, clientHeight } = element;
    const hasScrollAbove = scrollTop > EDGE_THRESHOLD;
    const hasScrollBelow =
      scrollTop + clientHeight < scrollHeight - EDGE_THRESHOLD;
    if (topFadeRef.current) {
      topFadeRef.current.style.opacity = hasScrollAbove ? "1" : "0";
    }
    if (bottomFadeRef.current) {
      bottomFadeRef.current.style.opacity = hasScrollBelow ? "1" : "0";
    }
  }, []);

  // Re-evaluate when the viewport resizes (e.g. window resize).
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [updateFades]);

  // Re-evaluate when the content changes (items added/removed).
  useEffect(() => {
    updateFades();
  }, [children, updateFades]);

  return (
    <div className="relative">
      <div
        ref={topFadeRef}
        aria-hidden
        className={clsx(
          FADE_BASE,
          "top-0 bg-gradient-to-b from-white via-white/70 to-transparent",
        )}
      />
      <div
        ref={scrollRef}
        onScroll={updateFades}
        className={clsx("overflow-y-auto", className)}
      >
        {children}
      </div>
      <div
        ref={bottomFadeRef}
        aria-hidden
        className={clsx(
          FADE_BASE,
          "bottom-0 bg-gradient-to-t from-white via-white/70 to-transparent",
        )}
      />
    </div>
  );
};
